import { useTranslation } from 'react-i18next';
import Breadcrumbs from '../../coreComponents/breadcrumbs';
import {
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  UncontrolledTooltip,
} from 'reactstrap';
import CommonCardHeader from '../../coreComponents/CommonCardHeader';
import {
  useFindAllScoreBoardQuery,
  useGetAllCoursesQuery,
} from '../../reduxToolkit/reducers/usersSlice';
import { useEffect, useMemo, useState } from 'react';

import { baseLang } from '../../lib/utils';

import { useLocation, useNavigate } from 'react-router-dom';

import { ScoreScrollTable } from '../../data/formsAndTable/tables/scoreScrollTable/ScoreScrollTable';
import { ICourse } from '../../types/Courses';
import { CustomLoader } from '../uiKits/customLoader/CustomLoader';

const Scorings = () => {
  const { t, i18n } = useTranslation();
  const loc = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(loc.search);
  const email = params.get('email');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [userFilter, setUserFilter] = useState<string>(email || '');
  const [sortKey, setSortKey] = useState('user.fullName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [coursesTabs, setCoursesTabs] = useState<ICourse[]>([]);
  const [activeCourseTab, setActiveCourseTab] = useState<ICourse | null>(null);
  const [preselectedUser, setPreselectedUser] = useState<{ label: string; value: string } | null>(
    null,
  );
  const pageLang = baseLang(i18n.language);
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data: coursesList } = useGetAllCoursesQuery({
    page: 1,
    perPage: 100,
    search: '',
    sortKey: 'id',
    sortDir: 'asc',
  });
  const {
    data: scoreBoard,
    isLoading: isScoreBoardLoading,
    isFetching: isScoreBoardFetching,
    error,
  } = useFindAllScoreBoardQuery(
    {
      page,
      perPage,
      search,
      userFilter,
      sortKey,
      sort: sortDir,
      courseId: activeCourseTab?.id,
    },
    {
      skip: !activeCourseTab || !activeCourseTab.id,
    },
  );

  useEffect(() => {
    if (coursesList && coursesList.length > 0) {
      console.log('coursesList:', coursesList);
      setActiveCourseTab(coursesList[0]);
      setCoursesTabs(coursesList);
    }
  }, [coursesList]);

  useEffect(() => {
    console.log('activeCourseTab:', activeCourseTab);
    console.log('coursesTabs:', coursesTabs);
  }, [activeCourseTab, coursesTabs]);
  useEffect(() => {
    if (scoreBoard?.users.length > 0 && email !== '') {
      const filteredUser = scoreBoard.users.find(
        (user: {
          id: number;
          firstName: string;
          lastName: string;
          email: string;
          telegram: string | null;
        }) => user.email === email,
      );

      setPreselectedUser(
        filteredUser
          ? {
              label: `${filteredUser.firstName} ${filteredUser.lastName}`,
              value: filteredUser.email,
            }
          : null,
      );
    }
  }, [scoreBoard?.columns, t, pageLang, viewerTz]);

  // Handle search
  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  // Handle sort
  const handleSort = (column: any, direction: 'asc' | 'desc') => {
    const key = column.sortField ?? column.selector;
    setSortKey(key);
    setSortDir(direction);
    setPage(1);
  };
  if (error) {
    return (
      <div className="alert alert-danger">
        {t('scoring.totalTable.errorLoadingTable', { err: error.toString() })}
      </div>
    );
  }

  const handleClearPreselectedUser = () => {
    setUserFilter('');
    setPreselectedUser(null);
    const params = new URLSearchParams(loc.search);
    params.delete('email');
    const newSearch = params.toString();
    const newPath = newSearch ? `${loc.pathname}?${newSearch}` : loc.pathname;
    navigate(newPath, { replace: true });
  };

  return (
    <>
      <Breadcrumbs mainTitle={t('scoring.sidebar.titleAvg')} />
      <Container fluid className="h-screen max-h-screen">
        <Row>
          {' '}
          <Card className="w-100">
            <CardBody>
              <Nav tabs className="simple-wrapper mt-3">
                {coursesTabs.map((item, index) => (
                  <NavItem key={index}>
                    <NavLink
                      tag="button"
                      type="button"
                      className={`txt-info !h4 f-w-500 ${activeCourseTab?.id === item.id ? 'active' : ''}`}
                      onClick={() => {
                        setActiveCourseTab(item);
                        localStorage.setItem('stored-score-tabs', `${item.id}`);
                      }}
                    >
                      <span id={`courses-tab-avg_id${item.id}`}>
                        {item.name.length > 20 ? `${item.name.slice(0, 20)}...` : item.name}
                      </span>
                      <UncontrolledTooltip
                        target={`courses-tab-avg_id${item.id}`}
                        placement="top"
                        container="body"
                        delay={{ show: 50, hide: 50 }}
                      >
                        {item.name}
                      </UncontrolledTooltip>
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
              <TabContent activeTab={String(activeCourseTab?.id)}>
                {!coursesTabs && <CustomLoader />}
                {coursesTabs.map((item, index) => {
                  return (
                    <TabPane
                      className={`fade ${activeCourseTab?.id === item.id ? 'show' : ''}`}
                      tabId={String(item.id)}
                      key={item.id}
                    >
                      <Col sm="12" className="d-flex">
                        {activeCourseTab?.id === item.id && (
                          <ScoreScrollTable
                            rows={scoreBoard?.rows || []}
                            page={page}
                            perPage={perPage}
                            setPage={setPage}
                            handleSearch={handleSearch}
                            handleSort={handleSort}
                            isScoreBoardLoading={isScoreBoardLoading}
                            isScoreBoardFetching={isScoreBoardFetching}
                            userFilter={userFilter}
                            setUserFilter={setUserFilter}
                            scoreBoard={scoreBoard}
                            preselectedUser={preselectedUser}
                            handleClearPreselectedUser={handleClearPreselectedUser}
                            hasSelector={true}
                            search={search}
                            sortKey={sortKey}
                            sortDir={sortDir}
                            setPerPage={setPerPage}
                          />
                        )}
                      </Col>
                    </TabPane>
                  );
                })}{' '}
              </TabContent>
            </CardBody>
          </Card>
        </Row>
      </Container>
    </>
  );
};
export default Scorings;
